import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import PublicLayout from '../components/PublicLayout';
import { api, useAuth } from '../App';
import { MapPin, Clock, IndianRupee, Bookmark, BookmarkCheck, ArrowLeft, Send, ExternalLink, CheckCircle, XCircle, ArrowRight } from 'lucide-react';

export default function OpportunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [applications, setApplications] = useState([]);
  const [loadingApps, setLoadingApps] = useState(false);
  
  const isOwner = user?.role === 'client' && opportunity?.client_id === user?.user_id;
  const isResearcher = user?.role === 'researcher';
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get(`/opportunities/${id}`);
        setOpportunity(response.data);
        
        // If owner, fetch applications
        if (user?.role === 'client') {
          setLoadingApps(true);
          try {
            const appsRes = await api.get(`/opportunities/${id}/applications`);
            setApplications(appsRes.data || []);
          } catch (e) {
            // Not owner or error
          } finally {
            setLoadingApps(false);
          }
        }
      } catch (error) {
        console.error('Error fetching opportunity:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, user]);
  
  const toggleSave = async () => {
    try {
      if (opportunity.is_saved) {
        await api.delete(`/opportunities/${id}/save`);
      } else {
        await api.post(`/opportunities/${id}/save`);
      }
      setOpportunity(prev => ({ ...prev, is_saved: !prev.is_saved }));
    } catch (error) {
      console.error('Error toggling save:', error);
    }
  };
  
  const handleApply = async (e) => {
    e.preventDefault();
    setApplying(true);
    try {
      await api.post('/applications', {
        opportunity_id: id,
        cover_letter: coverLetter
      });
      setOpportunity(prev => ({ ...prev, has_applied: true }));
      setShowApplyForm(false);
      setCoverLetter('');
    } catch (error) {
      console.error('Error applying:', error);
      alert(error.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplying(false);
    }
  };
  
  const updateApplicationStatus = async (appId, status) => {
    try {
      await api.put(`/applications/${appId}/status`, { status });
      setApplications(prev => 
        prev.map(app => app.application_id === appId ? { ...app, status } : app)
      );
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };
  
  const Layout = user ? DashboardLayout : PublicLayout;
  
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-800"></div>
        </div>
      </Layout>
    );
  }
  
  if (!opportunity) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-neutral-500">Opportunity not found</p>
          <button onClick={() => navigate(-1)} className="mt-4 text-sm text-neutral-600 hover:underline">
            Go back
          </button>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        data-testid="back-button"
        className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 mb-6 transition-colors"
      >
        <ArrowLeft size={20} />
        Back
      </button>
      
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-neutral-200 p-8">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h1 
                  className="text-2xl lg:text-3xl font-bold mb-2"
                  style={{ fontFamily: 'Outfit' }}
                  data-testid="opportunity-title"
                >
                  {opportunity.title}
                </h1>
                <p className="text-neutral-500">
                  Posted by {opportunity.client?.name || 'Anonymous Client'}
                </p>
              </div>
              
              {isResearcher && (
                <button
                  data-testid="save-opportunity"
                  onClick={toggleSave}
                  className={`p-3 rounded-sm transition-colors ${
                    opportunity.is_saved
                      ? 'text-[#FF6B35] bg-[#FFF0EB]'
                      : 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100'
                  }`}
                >
                  {opportunity.is_saved ? <BookmarkCheck size={24} /> : <Bookmark size={24} />}
                </button>
              )}
            </div>
            
            <div className="flex flex-wrap gap-4 text-sm text-neutral-600 mb-6 pb-6 border-b border-neutral-200">
              <span className="flex items-center gap-2">
                <MapPin size={18} />
                {opportunity.location_type}
                {opportunity.location && ` - ${opportunity.location}`}
              </span>
              {opportunity.duration && (
                <span className="flex items-center gap-2">
                  <Clock size={18} />
                  {opportunity.duration}
                </span>
              )}
              {(opportunity.budget_min || opportunity.budget_max) && (
                <span className="flex items-center gap-2">
                  <IndianRupee size={18} />
                  {opportunity.budget_min && opportunity.budget_max 
                    ? `₹${opportunity.budget_min.toLocaleString()} - ₹${opportunity.budget_max.toLocaleString()}`
                    : opportunity.budget_max 
                      ? `Up to ₹${opportunity.budget_max.toLocaleString()}`
                      : `From ₹${opportunity.budget_min.toLocaleString()}`
                  }
                </span>
              )}
              <span className={`px-2 py-0.5 rounded-sm text-xs font-medium ${
                opportunity.status === 'open' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-neutral-100 text-neutral-600'
              }`}>
                {opportunity.status}
              </span>
            </div>
            
            <div className="mb-6">
              <h2 className="font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>
                Description
              </h2>
              <p className="text-neutral-700 whitespace-pre-wrap" data-testid="opportunity-description">
                {opportunity.description}
              </p>
            </div>
            
            {opportunity.requirements?.length > 0 && (
              <div className="mb-6">
                <h2 className="font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>
                  Requirements
                </h2>
                <ul className="space-y-2">
                  {opportunity.requirements.map((req, i) => (
                    <li key={i} className="flex items-start gap-2 text-neutral-700">
                      <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {opportunity.skills_needed?.length > 0 && (
              <div>
                <h2 className="font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>
                  Skills Needed
                </h2>
                <div className="flex flex-wrap gap-2">
                  {opportunity.skills_needed.map((skill, i) => (
                    <span 
                      key={i}
                      className="px-3 py-1.5 bg-neutral-100 text-neutral-700 text-sm"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Applications (for owner) */}
          {isOwner && (
            <div className="bg-white border border-neutral-200 p-8 mt-6">
              <h2 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Outfit' }}>
                Applications ({applications.length})
              </h2>
              
              {loadingApps ? (
                <div className="py-8 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0D7377] mx-auto"></div>
                </div>
              ) : applications.length === 0 ? (
                <p className="text-neutral-500 py-4">No applications yet</p>
              ) : (
                <div className="space-y-4">
                  {applications.map((app) => (
                    <div 
                      key={app.application_id}
                      data-testid={`application-${app.application_id}`}
                      className="border border-neutral-200 p-4"
                    >
                      <div className="flex items-start gap-4">
                        {app.researcher?.picture ? (
                          <img 
                            src={app.researcher.picture} 
                            alt={app.researcher.name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-[#E6F3F4] flex items-center justify-center text-[#0D7377] font-bold">
                            {app.researcher?.name?.[0]?.toUpperCase()}
                          </div>
                        )}
                        
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <Link 
                              to={`/profile/${app.researcher_id}`}
                              className="font-semibold hover:text-[#0D7377]"
                            >
                              {app.researcher?.name}
                            </Link>
                            <span className={`text-xs px-2 py-1 rounded-sm font-medium ${
                              app.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              app.status === 'accepted' ? 'bg-green-100 text-green-700' :
                              app.status === 'rejected' ? 'bg-red-100 text-red-700' :
                              'bg-neutral-100 text-neutral-600'
                            }`}>
                              {app.status}
                            </span>
                          </div>
                          
                          {app.profile?.headline && (
                            <p className="text-sm text-neutral-500">{app.profile.headline}</p>
                          )}
                          
                          {app.cover_letter && (
                            <p className="text-sm text-neutral-600 mt-2 bg-neutral-50 p-3">
                              {app.cover_letter}
                            </p>
                          )}
                          
                          {app.status === 'pending' && (
                            <div className="flex gap-2 mt-3">
                              <button
                                data-testid={`accept-${app.application_id}`}
                                onClick={() => updateApplicationStatus(app.application_id, 'accepted')}
                                className="btn-client px-4 py-1.5 text-sm"
                              >
                                Accept
                              </button>
                              <button
                                data-testid={`reject-${app.application_id}`}
                                onClick={() => updateApplicationStatus(app.application_id, 'rejected')}
                                className="px-4 py-1.5 text-sm border border-neutral-200 hover:bg-neutral-50"
                              >
                                Reject
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Sidebar */}
        <div className="lg:col-span-1">
          {/* Apply Card (for researchers) */}
          {isResearcher && (
            <div className="bg-white border border-neutral-200 p-6 mb-6">
              {opportunity.has_applied ? (
                <div className="text-center">
                  <CheckCircle className="text-green-500 mx-auto mb-2" size={32} />
                  <p className="font-semibold text-green-700">You've Applied</p>
                  <p className="text-sm text-neutral-500 mt-1">
                    Your application has been submitted
                  </p>
                  <Link
                    to="/researcher/applications"
                    className="text-sm text-[#FF6B35] font-medium hover:underline mt-3 inline-block"
                  >
                    View My Applications
                  </Link>
                </div>
              ) : opportunity.status !== 'open' ? (
                <div className="text-center">
                  <XCircle className="text-neutral-400 mx-auto mb-2" size={32} />
                  <p className="font-semibold text-neutral-600">Not Accepting Applications</p>
                  <p className="text-sm text-neutral-500 mt-1">
                    This opportunity is currently {opportunity.status}
                  </p>
                </div>
              ) : showApplyForm ? (
                <form onSubmit={handleApply}>
                  <h3 className="font-semibold mb-3" style={{ fontFamily: 'Outfit' }}>
                    Apply to this Opportunity
                  </h3>
                  <textarea
                    data-testid="cover-letter-input"
                    placeholder="Write a cover letter (optional)..."
                    value={coverLetter}
                    onChange={(e) => setCoverLetter(e.target.value)}
                    rows={5}
                    className="w-full px-3 py-2 border border-neutral-200 mb-3 resize-none"
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      data-testid="submit-application"
                      disabled={applying}
                      className="btn-researcher px-4 py-2 flex-1 flex items-center justify-center gap-2"
                    >
                      {applying ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <>
                          <Send size={16} />
                          Submit Application
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowApplyForm(false)}
                      className="px-4 py-2 border border-neutral-200 hover:bg-neutral-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <button
                  data-testid="apply-button"
                  onClick={() => setShowApplyForm(true)}
                  className="btn-researcher w-full py-3 flex items-center justify-center gap-2"
                >
                  <Send size={18} />
                  Apply Now
                </button>
              )}
            </div>
          )}
          
          {/* Public CTA */}
          {!user && (
            <div className="bg-white border border-neutral-200 p-6 mb-6" data-testid="public-cta-card">
              <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>
                Interested in this opportunity?
              </h3>
              <p className="text-sm text-neutral-500 mb-4">
                Sign up to apply, save opportunities, and message clients directly.
              </p>
              <button onClick={() => login('researcher')} data-testid="public-cta-join" className="btn-researcher w-full py-3 flex items-center justify-center gap-2 mb-2">
                Join as Researcher <ArrowRight size={16} />
              </button>
              <button onClick={() => login('client')} className="w-full py-2.5 text-sm text-[#0D7377] border border-[#0D7377] hover:bg-[#E6F3F4] flex items-center justify-center gap-2">
                Join as Client <ArrowRight size={16} />
              </button>
            </div>
          )}
          
          {/* Client Info */}
          <div className="bg-white border border-neutral-200 p-6">
            <h3 className="font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>
              About the Client
            </h3>
            <div className="flex items-center gap-3">
              {opportunity.client?.picture ? (
                <img 
                  src={opportunity.client.picture} 
                  alt={opportunity.client.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-[#E6F3F4] flex items-center justify-center text-[#0D7377] font-bold text-lg">
                  {opportunity.client?.name?.[0]?.toUpperCase()}
                </div>
              )}
              <div>
                <p className="font-semibold">{opportunity.client?.name}</p>
                <span className="badge-client text-xs">Client</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
